const messagesBySite = import.meta.glob('./src/messages/*.json', {eager: true, import: 'default'});
const faqBySite = import.meta.glob('./src/faq/*.json', {eager: true, import: 'default'});
const aboutBySite = import.meta.glob('./src/about/*.json', {eager: true, import: 'default'});

// Single source of truth for multi-tenant/multi-domain setup — add tenants
// or alias domains here. domains[0] is the canonical domain used as the API Referer.
// messages/faq/about are loaded from src/{messages,faq,about}/<site>.json by key.
const TENANT_META = {
    nightcore: {
        domains: ['nightcore.com', 'erocthgin.com'],
        gtagId: 'G-RKQ0RF6DJK',
        favicon: 'favicon.ico',
        sitemapEnvKey: 'SITEMAP_URL_NIGHTCORE',
    },
    lofitune: {
        domains: ['lofitune.com', 'enutifol.com'],
        gtagId: 'G-NMR71VEHZE',
        favicon: 'lofitune.ico',
        sitemapEnvKey: 'SITEMAP_URL_LOFI',
    },
    reverbslowed: {
        domains: ['reverbslowed.com'],
        gtagId: 'G-CFRH1EB6MT',
        favicon: 'reverbslowed.ico',
        sitemapEnvKey: 'SITEMAP_URL_SLOWED',
    },
};

export const TENANTS = Object.fromEntries(
    Object.entries(TENANT_META).map(([site, meta]) => [
        site,
        {
            ...meta,
            messages: messagesBySite[`./src/messages/${site}.json`],
            faq: faqBySite[`./src/faq/${site}.json`],
            about: aboutBySite[`./src/about/${site}.json`],
        },
    ])
);

export const DEFAULT_SITE = 'nightcore';

function normalizeHost(hostname) {
    return (hostname || '').split(':')[0].toLowerCase().replace(/^www\./, '');
}

// Matches against each tenant's configured domains — a domain must be
// listed to resolve, unlike the old substring-based check.
export function resolveSite(hostname) {
    const host = normalizeHost(hostname);
    for (const [site, config] of Object.entries(TENANTS)) {
        if (config.domains.some(domain => host === domain || host.endsWith(`.${domain}`))) {
            return site;
        }
    }
    return DEFAULT_SITE;
}

export function getTenantConfig(site) {
    return TENANTS[site] ?? TENANTS[DEFAULT_SITE];
}

export function getPrimaryDomain(site) {
    return getTenantConfig(site).domains[0];
}

// The tenant domain this request actually arrived on (alias-aware), so that
// e.g. a subscriber page on laknicek.erocthgin.com sends users to
// www.erocthgin.com and not www.nightcore.com. Falls back to the tenant's
// primary domain when the host isn't a configured domain (localhost, .test).
export function getRequestDomain(hostname) {
    const host = normalizeHost(hostname);
    const site = resolveSite(hostname);
    return getTenantConfig(site).domains.find(domain => host === domain || host.endsWith(`.${domain}`))
        ?? getPrimaryDomain(site);
}

// Canonical main-site URL for a request host. The Express redirect middleware
// (server/middleware/security.js) sends apex hosts to www, so pointing at the
// www form avoids a second redirect.
export function mainSiteUrl(hostname) {
    return `https://www.${getRequestDomain(hostname)}/`;
}

export function getAllDomains() {
    return Object.values(TENANTS).flatMap(config => config.domains);
}
